import React from 'react';
import { IonPage, IonContent, IonButton, IonGrid, IonRow, IonCol } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import landing from '../content/landing.json';
import { resetConsent } from '../utils/analytics';

const { hero, features, plans, faq } = landing;


const Home: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <IonPage>
      <Helmet>
        <title>QA Master – AI Test Automation: Write Tests in Plain English</title>
        <meta name="description" content="QA Master turns plain-English acceptance criteria into automated Playwright browser and API tests. AI writes the steps, runs them, records video and suggests fixes. Free plan available." />
        <link rel="canonical" href="https://qamaster.uk/" />
      </Helmet>
      <IonContent>
        <nav aria-label="Main navigation" style={{
          display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center',
          padding: '16px clamp(16px, 4vw, 32px)', maxWidth: 1200, margin: '0 auto'
        }}>
          <a href="/" aria-label="QA Master home" style={{
            fontSize: 24, fontWeight: 700, margin: 0, textDecoration: 'none',
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
          }}>QA Master</a>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <IonButton fill="clear" href="/manual" color="primary" style={{ fontWeight: 600 }}>User Manual</IonButton>
            {isAuthenticated ? (
              <IonButton href="/dashboard" style={{
                '--background': 'linear-gradient(135deg, #667eea, #764ba2)', fontWeight: 600
              }}>Dashboard</IonButton>
            ) : (
              <>
                <IonButton fill="outline" href="/login" color="primary">Sign In</IonButton>
                <IonButton href="/register" style={{
                  '--background': 'linear-gradient(135deg, #667eea, #764ba2)', fontWeight: 600
                }}>Get Started</IonButton>
              </>
            )}
          </div>
        </nav>

        <main>
          <section aria-label="Hero" style={{
            textAlign: 'center', padding: '80px 24px 60px',
            background: 'linear-gradient(180deg, #f5f5f5 0%, #fff 100%)'
          }}>
            <h1 style={{
              fontSize: 'clamp(34px, 7vw, 48px)', fontWeight: 800, lineHeight: 1.15, margin: '0 auto',
              maxWidth: 700,
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
            }}>
              {hero.title}
            </h1>
            <p style={{
              fontSize: 20, color: '#555', maxWidth: 600, margin: '20px auto 0', lineHeight: 1.5
            }}>
              {hero.subtitle}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', marginTop: 36 }}>
              <IonButton href={isAuthenticated ? '/dashboard' : '/register'} size="large" style={{
                '--background': 'linear-gradient(135deg, #667eea, #764ba2)',
                fontWeight: 700, '--border-radius': '10px',
                '--padding-start': '32px', '--padding-end': '32px'
              }}>
                {isAuthenticated ? 'Go to Dashboard' : 'Start Free'}
              </IonButton>
              <IonButton href="#pricing" size="large" fill="outline" color="primary" style={{
                '--border-radius': '10px', fontWeight: 600,
                '--padding-start': '32px', '--padding-end': '32px'
              }}>
                View Pricing
              </IonButton>
            </div>
          </section>

          <section id="demo" aria-labelledby="demo-heading" style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 24px 0' }}>
            <h2 id="demo-heading" style={{ textAlign: 'center', fontSize: 32, fontWeight: 700, color: '#333', marginBottom: 8 }}>
              See QA Master in Action
            </h2>
            <p style={{ textAlign: 'center', color: '#666', fontSize: 16, marginBottom: 24 }}>
              Watch a real test run, from plain-English criteria to recorded results.
            </p>
            <video
              controls
              muted
              playsInline
              preload="none"
              poster="/demo/qamaster-demo-poster.jpg"
              style={{ width: '100%', borderRadius: 12, boxShadow: '0 8px 32px rgba(102,126,234,0.25)', background: '#000', aspectRatio: '16 / 9' }}
            >
              <source src="/demo/qamaster-demo.mp4" type="video/mp4" />
              <track kind="captions" src="/demo/qamaster-demo.en.vtt" srcLang="en" label="English" />
              Your browser can't play this video. <a href="/demo/qamaster-demo.mp4">Download the QA Master demo</a>.
            </video>
          </section>

          <section aria-label="Features" style={{ maxWidth: 1100, margin: '0 auto', padding: '60px 24px' }}>
            <h2 style={{ textAlign: 'center', fontSize: 32, fontWeight: 700, color: '#333', marginBottom: 8 }}>
              Everything You Need for QA Automation
            </h2>
            <p style={{ textAlign: 'center', color: '#666', fontSize: 16, marginBottom: 48, maxWidth: 600, marginLeft: 'auto', marginRight: 'auto' }}>
              A complete platform for creating, running, and managing automated browser tests with AI assistance.
            </p>

            <IonGrid>
              <IonRow>
                {features.map(f => (
                  <IonCol key={f.title} size="12" sizeMd="6" sizeLg="4" style={{ marginBottom: 24 }}>
                    <article style={{
                      background: '#fff', borderRadius: 12, padding: 24, height: '100%',
                      boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
                      border: '1px solid #f0f0f0',
                      transition: 'box-shadow 0.2s, transform 0.2s'
                    }}>
                      <div style={{ fontSize: 36, marginBottom: 12 }} role="img" aria-label={f.title}>{f.icon}</div>
                      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#333', margin: '0 0 8px' }}>
                        {f.title}
                      </h3>
                      <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: 0 }}>
                        {f.description}
                      </p>
                    </article>
                  </IonCol>
                ))}
              </IonRow>
            </IonGrid>
          </section>

          <section id="pricing" aria-label="Pricing" style={{
            background: 'linear-gradient(180deg, #fff 0%, #f8f9ff 100%)',
            padding: '60px 24px 80px'
          }}>
            <h2 style={{ textAlign: 'center', fontSize: 32, fontWeight: 700, color: '#333', marginBottom: 8 }}>
              Simple, Transparent Pricing
            </h2>
            <p style={{ textAlign: 'center', color: '#666', fontSize: 16, marginBottom: 40 }}>
              Start free. Upgrade when you need more. Cancel anytime.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, justifyContent: 'center' }}>
              {plans.map(plan => {
                const pro = plan.name === 'Pro';
                return (
                  <div key={plan.name} style={{
                    flex: '1 1 300px', maxWidth: 400, background: '#fff', borderRadius: 16,
                    padding: 32, textAlign: 'center',
                    boxShadow: pro ? '0 4px 24px rgba(102,126,234,0.15)' : '0 1px 8px rgba(0,0,0,0.06)',
                    border: pro ? '2px solid #667eea' : '1px solid #e5e7eb'
                  }}>
                    <h3 style={{
                      display: 'inline-block', fontSize: 13, fontWeight: 600, marginBottom: 16,
                      padding: '4px 16px', borderRadius: 20,
                      background: pro ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#f3f4f6',
                      color: pro ? '#fff' : '#444'
                    }}>
                      {plan.name.toUpperCase()} PLAN
                    </h3>
                    <div style={{ marginBottom: 4 }}>
                      <span style={{ fontSize: 48, fontWeight: 800, color: '#333' }}>{plan.price}</span>
                      <span style={{ fontSize: 16, color: '#999' }}> {plan.period}</span>
                    </div>
                    <p style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>{plan.note}</p>

                    <ul style={{ textAlign: 'left', marginBottom: 24, listStyle: 'none', padding: 0 }}>
                      {plan.items.map(item => (
                        <li key={item} style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '6px 0', fontSize: 14, color: '#444'
                        }}>
                          <span style={{ color: '#22c55e', fontWeight: 700 }} aria-hidden="true">&#10003;</span>
                          {item}
                        </li>
                      ))}
                    </ul>

                    <IonButton
                      expand="block"
                      fill={pro ? 'solid' : 'outline'}
                      href={isAuthenticated ? (pro ? '/subscription' : '/dashboard') : '/register'}
                      style={pro ? {
                        '--background': 'linear-gradient(135deg, #667eea, #764ba2)',
                        fontWeight: 700, '--border-radius': '10px', fontSize: 16
                      } : { fontWeight: 700, '--border-radius': '10px', fontSize: 16 }}
                    >
                      {pro ? (isAuthenticated ? 'Subscribe Now' : 'Get Started') : (isAuthenticated ? 'Go to Dashboard' : 'Start Free')}
                    </IonButton>
                  </div>
                );
              })}
            </div>
          </section>

          <section aria-labelledby="faq-heading" style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px' }}>
            <h2 id="faq-heading" style={{ textAlign: 'center', fontSize: 32, fontWeight: 700, color: '#333', marginBottom: 32 }}>
              Frequently Asked Questions
            </h2>
            {faq.map(item => (
              <details key={item.q} style={{ borderBottom: '1px solid #eee', padding: '16px 0' }}>
                <summary style={{ fontSize: 17, fontWeight: 600, color: '#333', cursor: 'pointer' }}>{item.q}</summary>
                <p style={{ fontSize: 15, color: '#555', lineHeight: 1.6, margin: '12px 0 0' }}>{item.a}</p>
              </details>
            ))}
          </section>
        </main>

        <footer style={{
          textAlign: 'center', padding: '32px 24px',
          borderTop: '1px solid #eee', color: '#999', fontSize: 13
        }}>
          <nav aria-label="Footer" style={{ marginBottom: 8, display: 'flex', gap: 16, justifyContent: 'center' }}>
            <a href="/manual">User Manual</a>
            <a href="#pricing">Pricing</a>
            <a href="/register">Create a free account</a>
            <a href="/login">Sign in</a>
            <a href="#cookies" onClick={e => { e.preventDefault(); resetConsent(); }}>Cookie settings</a>
          </nav>
          &copy; {new Date().getFullYear()} QA Master. All rights reserved.
        </footer>
      </IonContent>
    </IonPage>
  );
};

export default Home;
